import React from 'react';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';
import Translations from 'modules/translations';
import Routes from 'modules/routes';
import type { ICreateProps } from 'modules/modal';
import CloseButtonHeader from 'modules/modal/components/closeButtonHeader';
import { getTrackingClass } from 'modules/tracking/clickTracking';
import { Spacing24, Spacing16 } from 'autogen/design-tokens/tokens';
import { Button } from 'components/Button';
import ButtonCTAGroup from 'components/ButtonCTAGroup';
import { Headline, Body } from 'components/generic/typography';
import InlineButton from 'components/generic/InlineButton';
import format from 'helpers/translations';
import type { MessageWithQueueCallbacks } from 'components/pages/Basket/components/BasketCheckoutActions/BasketCheckoutButton/BasketCheckoutMessagesButton';
import LinkActionButton from 'components/Button/LinkActionButton';
import Logo from 'components/Icon/Logo';

// #region Styles

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    width: 100%;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding:  ${({ theme }) => `0 ${theme.Spacing24} ${theme.Spacing24}`};
    max-width: 375px;
`;

const StyledLogo = styled(Logo)`
    width: 140px;
    margin-bottom: ${({ theme }) => theme.Spacing16};
`;

const StyledHeadline = styled(Headline)`
    text-align: center;
    margin-bottom: ${({ theme }) => theme.Spacing16};
`;

const StyledBody = styled(Body)`
    text-align: center;
`;

const StyledCTAGroup = styled(ButtonCTAGroup)`
    ${breakpoint.up(sizes.md)} {
        max-width: 375px;
    }
`;

const StyledInlineButton = styled(InlineButton)`
    margin-top: ${({ theme }) => theme.Spacing16};
`;

const Header = styled(CloseButtonHeader)`
    align-items: center;
`;

// #endregion Styles

export default ({ message }: { message: MessageWithQueueCallbacks }): JSX.Element => {
    const { nextMessageInQueue, loginLinkAction } = message;
    const {
        Headline: HeadlineTranslation,
        Explanation,
        ActionText,
        ContinueWithoutDiscount,
        LoginButton: LoginButtonText,
        BecomeMember
    } = Translations.Website.Basket.PotentialLoginDiscount;

    return (
        <>
            <Wrapper>
                <Container>
                    <StyledLogo type="clubHorizontal" />
                    <StyledHeadline size="M">{HeadlineTranslation}</StyledHeadline>
                    <StyledBody forwardedAs="div">{format(Explanation)}</StyledBody>
                    <StyledBody forwardedAs="div">{format(ActionText)}</StyledBody>
                    <StyledInlineButton
                        fontWeight="semibold"
                        className="tracking_potentialLoginDiscount_continueWithoutDiscount"
                        onClick={nextMessageInQueue}
                    >
                        {ContinueWithoutDiscount}
                    </StyledInlineButton>
                </Container>
                <StyledCTAGroup size="sm" background>
                    <Button
                        as="a"
                        href={Routes.Signup.Index}
                        variant="secondary"
                        size="lg"
                        className="tracking_potentialLoginDiscount_signupLink"
                    >
                        {BecomeMember}
                    </Button>
                    <LinkActionButton
                        action={loginLinkAction}
                        variant="primary"
                        size="lg"
                        className={getTrackingClass('potentialLoginDiscount', 'goToSSO')}
                    >
                        {LoginButtonText}
                    </LinkActionButton>
                </StyledCTAGroup>
            </Wrapper>
        </>
    );
};

export const PotentialLoginDiscountCreateProps: ICreateProps = {
    layout: {
        header: <Header />,
        customTheme: { minWidth: 'unset', maxWidth: '375px' }
    }
};
